import {
    Avatar, Box, Chip, CircularProgress, IconButton, InputAdornment,
    Paper, Table, TableBody, TableCell, TableContainer, TableHead,
    TablePagination, TableRow, TextField, Tooltip, Typography
} from "@mui/material"
import { Refresh, Search } from "@mui/icons-material"
import { FC, useCallback, useEffect, useState } from "react"
import PageTitle from "../Common/PageTitle"
import NoData from "../Common/NoData"
import { Apis, numberWithCommas } from "../../helpers"
import { TABLE_HEIGHT } from "../../configs"
import _ from 'lodash'

interface ReferralRow {
    _id: string
    referralCode: string
    amountEarned: number
    currentEarning: number
    status: 'active' | 'inactive'
    connectedAt: string
    referrerName: string
    referrerPhone?: string
    referrerEmail?: string
    referrerProfilePhotos?: any
    referredName: string
    referredPhone?: string
    referredEmail?: string
    referredProfilePhotos?: any
}

interface StateProps {
    referrals: ReferralRow[]
    loading: boolean
    refreshing: boolean
    searchVal: string
    page: number
    pageSize: number
    count: number
}

const STATUS_STYLE: Record<string, { color: string; border: string }> = {
    active: { color: '#2e7d32', border: '#2e7d32' },
    inactive: { color: '#757575', border: '#757575' },
}

const AdminReferrals: FC = () => {
    const [state, setState] = useState<StateProps>({
        referrals: [],
        loading: true,
        refreshing: false,
        searchVal: '',
        page: 0,
        pageSize: 25,
        count: 0
    })

    const { referrals, loading, refreshing, searchVal, page, pageSize, count } = state

    const fetchReferrals = ({ searchVal: sv, newPage, newPageSize, processVariable }: any) => {
        const Page = newPage != null && newPage > -1 ? newPage : state.page
        const PageSize = newPageSize || state.pageSize
        const Search = sv !== undefined ? sv : state.searchVal
        if (processVariable) setState(s => ({ ...s, [processVariable]: true }))
        const afterLoad = { loading: false, refreshing: false, page: Page, pageSize: PageSize, searchVal: Search }
        Apis({ module: 'Users', action: 'getReferrals', searchVal: Search, page: Page, pageSize: PageSize })
            .then((resp: any) => setState(s => ({ ...s, referrals: resp.referrals || [], count: resp.count ?? s.count, ...afterLoad })))
            .catch(() => setState(s => ({ ...s, ...afterLoad })))
    }

    const startSearch = useCallback(_.debounce((val: string) => fetchReferrals({ searchVal: val, newPage: 0, processVariable: 'loading' }), 600), [])

    useEffect(() => {
        fetchReferrals({ processVariable: 'loading' })
        // eslint-disable-next-line
    }, [])

    return (
        <Box sx={{ pr: 5, height: '100%' }}>

            <PageTitle title="Referrals" />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, mt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    {count > 0 && (
                        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                            {count.toLocaleString()} total
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                        size="small"
                        placeholder="Search..."
                        variant="outlined"
                        onChange={e => startSearch(e.target.value)}
                        sx={{ width: 240, '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: 2 } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ fontSize: 17, color: 'text.disabled' }} />
                                </InputAdornment>
                            )
                        }}
                    />
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={() => fetchReferrals({ processVariable: 'refreshing' })} disabled={refreshing}>
                            {refreshing ? <CircularProgress size={18} /> : <Refresh sx={{ fontSize: 20 }} />}
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {!referrals.length || loading ?
                <NoData message="No referrals found." loading={loading} searchVal={searchVal} /> :
                <Paper sx={{ width: '100%' }} elevation={0} variant="outlined">
                    <TableContainer sx={{ maxHeight: TABLE_HEIGHT }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}></TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Referrer</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Referred</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Code</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Total Earned</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Balance</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Connected</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {referrals.map(r => {
                                    const chipStyle = STATUS_STYLE[r.status] || STATUS_STYLE.inactive
                                    return (
                                        <TableRow hover key={r._id}>
                                            <TableCell>
                                                <Avatar src={r.referrerProfilePhotos?.small?.url} sx={{ width: 28, height: 28, fontSize: 12 }} />
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>
                                                <Typography sx={{ fontSize: 13 }}>{r.referrerName?.trim() || '—'}</Typography>
                                                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{r.referrerPhone || r.referrerEmail || '—'}</Typography>
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>
                                                <Typography sx={{ fontSize: 13 }}>{r.referredName?.trim() || '—'}</Typography>
                                                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{r.referredPhone || r.referredEmail || '—'}</Typography>
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>{r.referralCode || '—'}</TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>Ksh {numberWithCommas(r.amountEarned || 0)}</TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>Ksh {numberWithCommas(r.currentEarning || 0)}</TableCell>
                                            <TableCell sx={{ fontSize: 13 }}>
                                                {r.connectedAt
                                                    ? new Date(r.connectedAt).toLocaleDateString('en-KE', { year: 'numeric', month: 'short', day: 'numeric' })
                                                    : '—'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={r.status}
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{
                                                        fontSize: 11,
                                                        color: chipStyle.color,
                                                        borderColor: chipStyle.border,
                                                        height: 20,
                                                        textTransform: 'capitalize'
                                                    }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={count}
                        page={page}
                        onPageChange={(_, newPage) => fetchReferrals({ newPage, processVariable: 'loading' })}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={e => fetchReferrals({ newPage: 0, newPageSize: parseInt(e.target.value, 10), processVariable: 'loading' })}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </Paper>
            }
        </Box>
    )
}

export default AdminReferrals
